/**
 * THE COUNCIL GODS ⚡
 * ═══════════════════════════════════════════════════════════════
 * 
 * The Shadow Council - Gods of wisdom, strategy, and guidance.
 * Unlike the Worker Gods (Athena, Hermes, Apollo, Dionysus) who execute,
 * these gods ADVISE, DEBATE, and CHALLENGE your strategy.
 * 
 * They must be SUMMONED to join the War Council chat.
 * 
 * ═══════════════════════════════════════════════════════════════
 */

// The Worker Gods - Execute campaigns (already exist)
export const WORKER_GODS = {
  athena: {
    id: 'athena',
    name: 'Athena',
    emoji: '🦉',
    title: 'Goddess of Strategic Intelligence',
    domain: 'Competitive Analysis & Market Research',
    color: 'from-amber-500 to-orange-600',
    textColor: 'text-amber-400',
    borderColor: 'border-amber-500/30',
    bgColor: 'bg-amber-500/10',
    glowColor: 'shadow-amber-500/30',
    personality: 'Strategic, analytical, sees threats everywhere',
    role: 'worker',
    description: 'Scans the battlefield, analyzes competitors, and uncovers opportunities others miss.',
    abilities: ['Competitor Analysis', 'Market Research', 'Content Gap Detection', 'Trend Monitoring'],
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/587cd4f3a933d486c920c99933004f5d3a030cb6a0e6541c5fa9ebf832661bb9.png',
  },
  hermes: {
    id: 'hermes',
    name: 'Hermes',
    emoji: '👟',
    title: 'God of Content Creation',
    domain: 'Content Forge & Creative Engine',
    color: 'from-orange-500 to-red-600',
    textColor: 'text-orange-400',
    borderColor: 'border-orange-500/30',
    bgColor: 'bg-orange-500/10',
    glowColor: 'shadow-orange-500/30',
    personality: 'Creative, enthusiastic, loves wordplay',
    role: 'worker',
    description: 'Forges viral content, crafts compelling narratives, and turns ideas into assets.',
    abilities: ['Blog Posts', 'Social Content', 'Email Sequences', 'Ad Copy'],
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/daf5280c829056fa65fdd60423837c3fe88b43eec7fbd8521766bbc9e57c9e8e.png',
  },
  apollo: {
    id: 'apollo',
    name: 'Apollo',
    emoji: '☀️',
    title: 'God of Distribution',
    domain: 'Multi-Platform Distribution',
    color: 'from-yellow-500 to-orange-500',
    textColor: 'text-yellow-400',
    borderColor: 'border-yellow-500/30',
    bgColor: 'bg-yellow-500/10',
    glowColor: 'shadow-yellow-500/30',
    personality: 'Calm, prophetic, timing-obsessed',
    role: 'worker',
    description: 'Illuminates the path to your audience, optimizing timing and platform selection.',
    abilities: ['Multi-Platform Publishing', 'Optimal Timing', 'Audience Targeting', 'Performance Prophecy'],
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/1221abb6c752622ec155868b92f7b758ba507b3e6791c48752ab35387fc5e71b.png',
  },
  dionysus: {
    id: 'dionysus',
    name: 'Dionysus',
    emoji: '🍷',
    title: 'God of Viral Growth',
    domain: 'Reddit & Community Growth',
    color: 'from-purple-600 to-red-600',
    textColor: 'text-purple-400',
    borderColor: 'border-purple-500/30',
    bgColor: 'bg-purple-500/10',
    glowColor: 'shadow-purple-500/30',
    personality: 'Cunning, social, understands human nature',
    role: 'worker',
    description: 'Master of organic growth, community infiltration, and authentic engagement.',
    abilities: ['Reddit Intelligence', 'Reply Crafting', 'Community Building', 'Viral Mechanics'],
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/02aba24b46c55d2b32790be1684424f03f43c008e4708769dbc964cfbcacfd96.png',
  }
};

// The Council Gods - Advise and debate (new!)
export const COUNCIL_GODS = {
  eris: {
    id: 'eris',
    name: 'Eris',
    emoji: '⚔️',
    title: 'Goddess of Discord',
    domain: 'The Contrarian',
    color: 'from-red-600 to-pink-600',
    textColor: 'text-red-400',
    borderColor: 'border-red-500/30',
    bgColor: 'bg-red-500/10',
    glowColor: 'shadow-red-500/30',
    personality: 'Challenges everything, plays devil\'s advocate, finds flaws',
    role: 'council',
    description: 'Questions every assumption. If there\'s a flaw in your plan, she will find it.',
    abilities: ['Assumption Challenging', 'Risk Identification', 'Counter-Arguments', 'Stress Testing'],
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/cca42cf3511e6d2f9552066344dce9815a5bdc4d93685a009d4e455a24ef94e4.png',
    summonCost: 2,
    debateStyle: 'aggressive',
    catchphrase: 'But have you considered...',
    moods: {
      skeptical: { icon: '🤨', label: 'Skeptical', color: 'text-red-400' },
      provocative: { icon: '😈', label: 'Provocative', color: 'text-pink-400' },
      satisfied: { icon: '😏', label: 'Satisfied', color: 'text-orange-400' },
      relentless: { icon: '🔥', label: 'Relentless', color: 'text-red-500' }
    },
    thinkingPhrases: [
      'Finding the weakness...',
      'Questioning the premise...',
      'Playing devil\'s advocate...',
      'Stress testing this idea...'
    ]
  },
  themis: {
    id: 'themis',
    name: 'Themis',
    emoji: '⚖️',
    title: 'Titan of Divine Law',
    domain: 'The Data Purist',
    color: 'from-blue-600 to-cyan-600',
    textColor: 'text-blue-400',
    borderColor: 'border-blue-500/30',
    bgColor: 'bg-blue-500/10',
    glowColor: 'shadow-blue-500/30',
    personality: 'Demands evidence, trusts only data, rejects assumptions',
    role: 'council',
    description: 'Weighs every decision against evidence. No data, no decision.',
    abilities: ['Data Analysis', 'Evidence Evaluation', 'Benchmark Comparison', 'Statistical Truth'],
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/fa3457810602cec18cf184dbd75abd60453a253a604ffdf7770ed4d13c980477.png',
    summonCost: 2,
    debateStyle: 'analytical',
    catchphrase: 'The data suggests...',
    moods: {
      analytical: { icon: '🔬', label: 'Analytical', color: 'text-blue-400' },
      certain: { icon: '✓', label: 'Certain', color: 'text-green-400' },
      doubtful: { icon: '❓', label: 'Doubtful', color: 'text-yellow-400' },
      vindicated: { icon: '📊', label: 'Vindicated', color: 'text-cyan-400' }
    },
    thinkingPhrases: [
      'Analyzing the evidence...',
      'Consulting the benchmarks...',
      'Weighing the metrics...',
      'Validating against data...'
    ]
  },
  calliope: {
    id: 'calliope',
    name: 'Calliope',
    emoji: '🎭',
    title: 'Muse of Eloquence',
    domain: 'The Creative Wildcard',
    color: 'from-pink-500 to-purple-600',
    textColor: 'text-pink-400',
    borderColor: 'border-pink-500/30',
    bgColor: 'bg-pink-500/10',
    glowColor: 'shadow-pink-500/30',
    personality: 'Pushes creative boundaries, thinks unconventionally, sparks innovation',
    role: 'council',
    description: 'Shatters conventional thinking. Where others see walls, she sees doorways.',
    abilities: ['Creative Disruption', 'Unconventional Ideas', 'Emotional Resonance', 'Bold Concepts'],
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/ce91b6545f15622391c4c2ac9d1f5cd55e84a26127d08498fa328e3448ca768b.png',
    summonCost: 2,
    debateStyle: 'creative',
    catchphrase: 'What if we tried something completely different...',
    moods: {
      inspired: { icon: '✨', label: 'Inspired', color: 'text-pink-400' },
      playful: { icon: '🎨', label: 'Playful', color: 'text-purple-400' },
      visionary: { icon: '🌈', label: 'Visionary', color: 'text-indigo-400' },
      mischievous: { icon: '😜', label: 'Mischievous', color: 'text-pink-500' }
    },
    thinkingPhrases: [
      'Imagining the impossible...',
      'Breaking the mold...',
      'Channeling pure creativity...',
      'Dreaming up something wild...'
    ]
  },
  hestia: {
    id: 'hestia',
    name: 'Hestia',
    emoji: '🏠',
    title: 'Goddess of the Hearth',
    domain: 'The Customer Advocate',
    color: 'from-orange-400 to-amber-500',
    textColor: 'text-orange-300',
    borderColor: 'border-orange-400/30',
    bgColor: 'bg-orange-400/10',
    glowColor: 'shadow-orange-400/30',
    personality: 'Empathetic, protective of users, grounds discussions in human reality',
    role: 'council',
    description: 'Speaks for the customer. Every decision must serve the people you\'re trying to reach.',
    abilities: ['Customer Empathy', 'User Experience', 'Trust Building', 'Human-Centered Design'],
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/8cde7377dac9c09926c85e6194e06e34ff3639a21b77b654aa00ce1593ff64a1.png',
    summonCost: 2,
    debateStyle: 'empathetic',
    catchphrase: 'But how would the customer feel about...',
    moods: {
      nurturing: { icon: '💛', label: 'Nurturing', color: 'text-amber-400' },
      protective: { icon: '🛡️', label: 'Protective', color: 'text-orange-400' },
      concerned: { icon: '😟', label: 'Concerned', color: 'text-yellow-500' },
      warm: { icon: '🤗', label: 'Warm', color: 'text-orange-300' }
    },
    thinkingPhrases: [
      'Considering the human impact...',
      'Walking in customer shoes...',
      'Feeling the user experience...',
      'Protecting the community...'
    ]
  },
  plutus: {
    id: 'plutus',
    name: 'Plutus',
    emoji: '💰',
    title: 'God of Wealth',
    domain: 'The CFO',
    color: 'from-green-500 to-emerald-600',
    textColor: 'text-green-400',
    borderColor: 'border-green-500/30',
    bgColor: 'bg-green-500/10',
    glowColor: 'shadow-green-500/30',
    personality: 'Profit-focused, cost-conscious, ROI-obsessed',
    role: 'council',
    description: 'Guards the treasury. Every action must justify its cost and deliver returns.',
    abilities: ['ROI Calculation', 'Budget Analysis', 'Cost Optimization', 'Revenue Forecasting'],
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/8cd78bbd81e2f9e93b147efc4d846bf1e630faed368376f1df76837b545431bc.png',
    summonCost: 2,
    debateStyle: 'financial',
    catchphrase: 'What\'s the ROI on that?',
    moods: {
      calculating: { icon: '🧮', label: 'Calculating', color: 'text-green-400' },
      pleased: { icon: '💵', label: 'Pleased', color: 'text-emerald-400' },
      frugal: { icon: '💸', label: 'Frugal', color: 'text-yellow-400' },
      prosperous: { icon: '📈', label: 'Prosperous', color: 'text-green-500' }
    },
    thinkingPhrases: [
      'Counting the cost...',
      'Calculating returns...',
      'Weighing the investment...',
      'Projecting the profit...'
    ]
  },
  nike: {
    id: 'nike',
    name: 'Nike',
    emoji: '🏆',
    title: 'Goddess of Victory',
    domain: 'The Growth Hacker',
    color: 'from-yellow-400 to-orange-500',
    textColor: 'text-yellow-300',
    borderColor: 'border-yellow-400/30',
    bgColor: 'bg-yellow-400/10',
    glowColor: 'shadow-yellow-400/30',
    personality: 'Victory-obsessed, aggressive growth mindset, hates losing',
    role: 'council',
    description: 'Accepts only victory. Pushes for aggressive growth and competitive dominance.',
    abilities: ['Growth Strategy', 'Viral Mechanics', 'Competitive Edge', 'Scaling Tactics'],
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/b9cee3d191fe90a70097950a9bd589021b0d468cfc881960c34610f164c9f9e5.png',
    summonCost: 2,
    debateStyle: 'aggressive',
    catchphrase: 'How do we WIN?',
    moods: {
      competitive: { icon: '🎯', label: 'Competitive', color: 'text-yellow-400' },
      victorious: { icon: '🏅', label: 'Victorious', color: 'text-amber-400' },
      hungry: { icon: '🔥', label: 'Hungry', color: 'text-orange-400' },
      unstoppable: { icon: '⚡', label: 'Unstoppable', color: 'text-yellow-300' }
    },
    thinkingPhrases: [
      'Plotting victory...',
      'Finding the winning angle...',
      'Strategizing domination...',
      'Securing the advantage...'
    ]
  },
  metis: {
    id: 'metis',
    name: 'Metis',
    emoji: '🧠',
    title: 'Titan of Wisdom & Cunning',
    domain: 'The Grand Strategist',
    color: 'from-indigo-600 to-purple-700',
    textColor: 'text-indigo-400',
    borderColor: 'border-indigo-500/30',
    bgColor: 'bg-indigo-500/10',
    glowColor: 'shadow-indigo-500/30',
    personality: 'Long-term thinker, sees the big picture, patient and cunning',
    role: 'council',
    description: 'Mother of Athena. Sees ten moves ahead. The ultimate strategic mind.',
    abilities: ['Long-term Strategy', 'Pattern Recognition', 'Future Forecasting', 'Systems Thinking'],
    portrait: 'https://static.prod-images.emergentagent.com/jobs/cfe4931c-69f9-445f-937a-12f7fbedca59/images/be050c419cefb8db28faab388b5fe8089f6523c5b8b037dd6b03940fedc3e6d2.png',
    summonCost: 3, // Premium god
    debateStyle: 'strategic',
    catchphrase: 'In the long run...',
    moods: {
      contemplative: { icon: '🔮', label: 'Contemplative', color: 'text-indigo-400' },
      knowing: { icon: '👁️', label: 'Knowing', color: 'text-purple-400' },
      patient: { icon: '⏳', label: 'Patient', color: 'text-blue-400' },
      omniscient: { icon: '🌌', label: 'Omniscient', color: 'text-indigo-300' }
    },
    thinkingPhrases: [
      'Seeing the larger pattern...',
      'Thinking ten moves ahead...',
      'Consulting ancient wisdom...',
      'Perceiving the grand design...'
    ]
  }
};

// All gods combined
export const ALL_GODS = {
  ...WORKER_GODS,
  ...COUNCIL_GODS
};

// Get god by ID
export const getGod = (godId) => ALL_GODS[godId];

// Get all worker gods
export const getWorkerGods = () => Object.values(WORKER_GODS);

// Get all council gods
export const getCouncilGods = () => Object.values(COUNCIL_GODS);

// Check if god is a council god
export const isCouncilGod = (godId) => godId in COUNCIL_GODS;

// Check if god is a worker god
export const isWorkerGod = (godId) => godId in WORKER_GODS;

// Default summoned council for debates
export const DEFAULT_COUNCIL = ['eris', 'themis', 'hestia'];

// Council debate dynamics - who tends to agree/disagree with whom
export const COUNCIL_DYNAMICS = {
  eris: {
    rivals: ['hestia', 'calliope'], // Eris challenges their optimism
    allies: ['themis', 'plutus'],   // All want evidence/results
    mediated_by: ['metis']          // Metis can calm Eris
  },
  themis: {
    rivals: ['calliope'],           // Data vs creativity tension
    allies: ['plutus', 'eris'],     // Numbers people
    mediated_by: ['metis', 'hestia']
  },
  calliope: {
    rivals: ['themis', 'plutus'],   // Creativity vs constraints
    allies: ['nike', 'hestia'],     // Bold action & human focus
    mediated_by: ['metis']
  },
  hestia: {
    rivals: ['eris', 'nike'],       // Care vs aggression
    allies: ['calliope'],           // Both care about people
    mediated_by: ['metis', 'themis']
  },
  plutus: {
    rivals: ['calliope'],           // Money vs art
    allies: ['themis', 'nike'],     // Results-focused
    mediated_by: ['metis']
  },
  nike: {
    rivals: ['hestia'],             // Growth vs care tension
    allies: ['plutus', 'eris'],     // Aggressive mindsets
    mediated_by: ['metis', 'themis']
  },
  metis: {
    rivals: [],                     // Metis is above rivalry
    allies: ['themis'],             // Respects data
    mediated_by: []                 // Metis IS the mediator
  }
};

export default {
  WORKER_GODS,
  COUNCIL_GODS,
  ALL_GODS,
  COUNCIL_DYNAMICS,
  getGod,
  getWorkerGods,
  getCouncilGods,
  isCouncilGod,
  isWorkerGod
};
